import axios from "axios";
import { getApiBase, getConnectionHint } from "@/apiBase";
import { getAdminToolsUnlockToken } from "@/lib/roles";
import { recordLastBackupMeta } from "@/lib/lastBackupMeta";

const client = axios.create({ baseURL: getApiBase() || undefined });

client.interceptors.request.use((config) => {
  const token = localStorage.getItem("token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

function adminToolsAuthHeaders() {
  const unlock = getAdminToolsUnlockToken();
  const headers = {};
  const token = localStorage.getItem("token");
  if (token) headers.Authorization = `Bearer ${token}`;
  if (unlock) headers["X-Admin-Tools-Token"] = unlock;
  return headers;
}

/** Mensaje legible (evita overlay «Error de script» cuando no hay `message`). */
export function errorToUserMessage(reason) {
  if (reason == null) return "Error desconocido.";
  if (typeof reason === "string") return reason.trim() || "Error desconocido.";
  if (reason instanceof Error) {
    const m = (reason.message || "").trim();
    return m || reason.name || "Error desconocido.";
  }
  if (typeof reason === "object" && typeof reason.message === "string" && reason.message.trim()) {
    return reason.message.trim();
  }
  try {
    const s = JSON.stringify(reason);
    return s && s !== "{}" ? s : "Error desconocido.";
  } catch {
    return "Error desconocido.";
  }
}

async function blobToTextSafe(blob) {
  if (blob == null) return "";
  if (typeof blob.text === "function") {
    return blob.text();
  }
  return new Promise((resolve, reject) => {
    const fr = new FileReader();
    fr.onload = () => resolve(String(fr.result ?? ""));
    fr.onerror = () => reject(new Error("No se pudo leer la respuesta"));
    fr.readAsText(blob);
  });
}

function parseJsonDetailText(text) {
  if (!text) return null;
  try {
    const j = JSON.parse(text);
    if (typeof j.detail === "string") return j.detail;
    if (Array.isArray(j.detail)) {
      return j.detail
        .map((x) => (typeof x === "object" && x?.msg ? x.msg : String(x)))
        .join(". ");
    }
  } catch {
    const t = text.trim();
    if (t.startsWith("<!") || t.includes("<!DOCTYPE")) {
      return (
        "El servidor devolvió HTML en lugar de JSON. " +
        `Compruebe que el API esté activo (${getConnectionHint()}).`
      );
    }
    if (t.length > 0 && t.length < 500) return t;
  }
  return null;
}

/** Extrae mensaje de error de respuestas axios (JSON o blob JSON). */
export async function extractAxiosErrorMessage(err) {
  if (!err || typeof err !== "object") return errorToUserMessage(err);
  const data = err.response?.data;
  if (data instanceof Blob) {
    try {
      const parsed = parseJsonDetailText(await blobToTextSafe(data));
      if (parsed) return parsed;
    } catch {
      /* ignore */
    }
  }
  if (data && typeof data === "object" && typeof data.detail === "string") return data.detail;
  if (Array.isArray(data?.detail)) {
    return data.detail
      .map((x) => {
        if (typeof x === "string") return x;
        if (typeof x === "object" && x?.msg) return x.msg;
        return String(x);
      })
      .join(". ");
  }
  return errorToUserMessage(err);
}

function filenameFromContentDisposition(header) {
  if (!header || typeof header !== "string") return null;
  const m = /filename\*?=(?:UTF-8''|")?([^";\n]+)/i.exec(header);
  if (!m) return null;
  try {
    return decodeURIComponent(m[1].replace(/"/g, "").trim());
  } catch {
    return m[1].replace(/"/g, "").trim();
  }
}

function sanitizeDownloadFilename(name) {
  const raw = (name || "").trim().replace(/^.*[/\\]/, "");
  const cleaned = raw.replace(/[<>:"|?*\u0000-\u001f]/g, "_").slice(0, 200);
  return cleaned || "descarga.bin";
}

export function saveBlobAsFile(blob, filename) {
  const safeName = sanitizeDownloadFilename(filename);
  let url;
  try {
    url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = safeName;
    link.rel = "noopener";
    link.style.cssText = "position:fixed;left:-9999px;top:0;opacity:0;";
    document.body.appendChild(link);
    try {
      link.dispatchEvent(new MouseEvent("click", { bubbles: true, cancelable: true, view: window }));
    } catch {
      link.click();
    }
    window.setTimeout(() => {
      try {
        document.body.removeChild(link);
      } catch {
        /* ya removido */
      }
      try {
        URL.revokeObjectURL(url);
      } catch {
        /* ignore */
      }
    }, 2000);
  } catch (e) {
    try {
      if (url) URL.revokeObjectURL(url);
    } catch {
      /* ignore */
    }
    throw new Error(errorToUserMessage(e) || "No se pudo iniciar la descarga del archivo.");
  }
}

async function parseFetchErrorResponse(response) {
  const fallback = `Error del servidor (${response.status})`;
  try {
    const text = typeof response.text === "function" ? await response.text() : "";
    return parseJsonDetailText(text) || fallback;
  } catch {
    return fallback;
  }
}

export async function downloadBackupJson() {
  const base = getApiBase();
  if (!base) {
    throw new Error(
      `No está configurada la URL del API (${getConnectionHint()}). Use npm start con proxy /api o defina REACT_APP_BACKEND_URL.`,
    );
  }

  const headers = adminToolsAuthHeaders();

  const url = `${String(base).replace(/\/+$/, "")}/import-export/backup.json`;

  let response;
  try {
    response = await fetch(url, {
      method: "GET",
      headers,
      credentials: "same-origin",
    });
  } catch (err) {
    const hint = err?.message ? `: ${err.message}` : "";
    throw new Error(`No se pudo conectar con el servidor${hint}. ${getConnectionHint()}`);
  }

  if (!response.ok) {
    throw new Error(await parseFetchErrorResponse(response));
  }

  let buffer;
  try {
    buffer = await response.arrayBuffer();
  } catch (e) {
    throw new Error(`No se pudo leer el archivo descargado: ${errorToUserMessage(e)}`);
  }
  if (!buffer || buffer.byteLength === 0) {
    throw new Error("El respaldo recibido está vacío.");
  }

  const blob = new Blob([buffer], {
    type: response.headers.get("content-type") || "application/json;charset=utf-8",
  });

  const stamp = new Date().toISOString().slice(0, 10);
  const filename =
    filenameFromContentDisposition(response.headers.get("content-disposition")) ||
    `icjeme_respaldo_${stamp}.json`;

  try {
    saveBlobAsFile(blob, filename);
  } catch (e) {
    throw new Error(errorToUserMessage(e));
  }

  // Solo tras descarga exitosa (no se registra si falló arriba).
  return recordLastBackupMeta({ fileName: filename });
}

export async function downloadBlob(path, filename) {
  const base = getApiBase();
  if (!base) {
    throw new Error(`No está configurada la URL del API (${getConnectionHint()}).`);
  }

  let res;
  try {
    res = await client.get(path, {
      responseType: "blob",
      validateStatus: () => true,
    });
  } catch (err) {
    throw new Error(errorToUserMessage(err) || "No se pudo descargar el archivo");
  }

  if (res.status < 200 || res.status >= 300) {
    const msg =
      (await extractAxiosErrorMessage({ response: { data: res.data, status: res.status } })) ||
      `Error del servidor (${res.status})`;
    throw new Error(msg);
  }

  const data = res.data instanceof Blob ? res.data : new Blob([res.data != null ? res.data : ""]);
  const fromHeader = filenameFromContentDisposition(res.headers["content-disposition"]);
  try {
    saveBlobAsFile(data, fromHeader || filename);
  } catch (e) {
    throw new Error(errorToUserMessage(e));
  }
}

export async function downloadTemplate(resource) {
  await downloadBlob(`/import-export/template/${resource}`, `plantilla_${resource}.xlsx`);
}

export async function downloadExport(resource, format) {
  const ext = format === "xlsx" ? "xlsx" : format === "csv" ? "csv" : "pdf";
  await downloadBlob(`/import-export/export/${resource}?format=${format}`, `export_${resource}.${ext}`);
}

export async function restoreBackupJson(file) {
  const fd = new FormData();
  fd.append("file", file);
  const res = await client.post("/import-export/restore.json", fd, {
    headers: {
      "Content-Type": "multipart/form-data",
      ...adminToolsAuthHeaders(),
    },
  });
  return res.data;
}

export async function uploadImport(resource, file) {
  const fd = new FormData();
  fd.append("file", file);
  const res = await client.post(`/import-export/import/${resource}`, fd, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return res.data;
}
